import React from 'react'
import { Marquee } from '@devnomic/marquee'
import classNames from 'classnames'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import useWindowSize from '@/utils/useWindowSize'

interface ScrollingTitlesProps {
  className?: string
  spritesheetImage?: string | null
}

const DEFAULT_SPRITESHEET =
  'https://images.angelstudios.com/image/upload/v1770409689/angel-studios/guild/css_sprites_6.webp'

// Sprite grid offset for a given column and row position (5 cols x 3 rows)
function spritePosition(col: number, row: number) {
  return { x: -(10 + col * 220), y: -(10 + row * 320) }
}

// Distribute the 15 poster positions across 3 marquee columns
const firstColumn = [
  spritePosition(0, 0),
  spritePosition(1, 0),
  spritePosition(0, 2),
  spritePosition(2, 2),
  spritePosition(0, 1),
]
const secondColumn = [
  spritePosition(3, 1),
  spritePosition(4, 1),
  spritePosition(4, 0),
  spritePosition(2, 1),
  spritePosition(3, 0),
]
const thirdColumn = [
  spritePosition(1, 1),
  spritePosition(1, 2),
  spritePosition(2, 0),
  spritePosition(3, 2),
  spritePosition(4, 2),
]

export const ScrollingTitles: React.FC<ScrollingTitlesProps> = (props) => {
  const sprite = props.spritesheetImage || DEFAULT_SPRITESHEET
  const { innerWidth, innerHeight } = useWindowSize()
  const imageHeight = React.useMemo(() => {
    return innerWidth > 2000
      ? '400px'
      : innerWidth > 1300
      ? '300px'
      : innerWidth > 768
      ? '220px'
      : innerHeight > 670
      ? '165px'
      : '140px'
  }, [innerWidth, innerHeight])
  const imageWidth = React.useMemo(() => {
    return innerWidth > 2000
      ? '267px'
      : innerWidth > 1300
      ? '200px'
      : innerWidth > 768
      ? '147px'
      : innerHeight > 670
      ? '110px'
      : '93px'
  }, [innerWidth, innerHeight])
  const imageZoom = React.useMemo(() => {
    return innerWidth > 2000 ? 1.33 : innerWidth > 1300 ? 0 : innerWidth > 768 ? 0.73 : innerHeight > 670 ? 0.55 : 0.46
  }, [innerWidth, innerHeight])

  return (
    <div
      className={classNames(
        'z-10 mb-[45px] flex h-1/3 w-full justify-center gap-x-4 opacity-70 lg:my-0 lg:h-full lg:w-1/2 lg:justify-center lg:opacity-100',
        props.className,
      )}
    >
      <link rel="preload" href={sprite} as="image" fetchPriority="high"></link>
      <LazyHydrate whenVisible>
        <>
          {innerWidth && innerHeight && (
            <Marquee direction="up" reverse className="fade-marquee h-full w-1/3 animate-fadein md:w-auto">
              {firstColumn?.map((poster, index) => {
                const background = `url('${sprite}') ${poster.x}px ${poster.y}px`
                return (
                  <div
                    key={index}
                    style={{
                      height: imageHeight,
                      width: imageWidth,
                    }}
                    className="rounded-2xl"
                  >
                    <div
                      style={{
                        background,
                        zoom: imageZoom,
                      }}
                      className="pointer-events-none h-full w-full max-w-full touch-none select-none rounded-2xl bg-center"
                    />
                  </div>
                )
              })}
            </Marquee>
          )}
        </>
      </LazyHydrate>
      <LazyHydrate whenVisible>
        <>
          {innerWidth && innerHeight && (
            <Marquee
              direction="up"
              className="fade-marquee h-full w-1/3 animate-fadein [--duration:32s] md:w-auto"
              innerClassName="[--duration:32s]"
            >
              {secondColumn?.map((poster, index) => {
                const background = `url('${sprite}') ${poster.x}px ${poster.y}px`
                return (
                  <div
                    key={index}
                    style={{
                      height: imageHeight,
                      width: imageWidth,
                    }}
                    className="rounded-2xl"
                  >
                    <div
                      style={{
                        background,
                        zoom: imageZoom,
                      }}
                      className="pointer-events-none h-full w-full max-w-full touch-none select-none rounded-2xl bg-center "
                    />
                  </div>
                )
              })}
            </Marquee>
          )}
        </>
      </LazyHydrate>
      <LazyHydrate whenVisible>
        <>
          {innerWidth && innerHeight && (
            <Marquee direction="up" reverse className="fade-marquee w-1/3 animate-fadein md:w-auto">
              {thirdColumn?.map((poster, index) => {
                const background = `url('${sprite}') ${poster.x}px ${poster.y}px`
                return (
                  <div
                    key={index}
                    style={{
                      height: imageHeight,
                      width: imageWidth,
                    }}
                    className="rounded-2xl"
                  >
                    <div
                      style={{
                        background,
                        zoom: imageZoom,
                      }}
                      className="pointer-events-none h-full w-full max-w-full touch-none select-none rounded-2xl bg-center"
                    />
                  </div>
                )
              })}
            </Marquee>
          )}
        </>
      </LazyHydrate>
    </div>
  )
}
